import type { ReferencedChartTabProps, ReferencedMultiChartDataPoint } from "@wayflyer/flyui-dataviz";
// eslint-disable-next-line no-restricted-imports
import moment, { type Moment } from "moment";

import type { CustomTimeRange, TemporalResolution } from "../utils/types";
import { TemporalResolutions } from "../utils/types";
import type { DashboardOutput } from "../utils/fetch-api";
import { xDomainSteps, xFormatDate } from "../utils/custom-time-range-helpers";
import { MetricTypes } from "../autogen/metrics";
import { formatValue } from "../strings/type-formatting";
import { includeMetricDecimal } from "../utils/metrics";
import { getReferenceData } from "../overview/overview-datapoints-2";
import type { ProfitabilityDataPoint } from "../components/profitability-chart";
// import type { ReferencedChartTabProps } from "@wayflyer/flyui-dataviz";

export const getRevenueChartData = (
  timeRange: CustomTimeRange,
  data: DashboardOutput,
  temporalResolution: TemporalResolution,
  inputTabs: ReferencedChartTabProps[],
  tableTabs: ReferencedChartTabProps[],
  groups: string[]
) => {
  const steps = xDomainSteps(timeRange.start, timeRange.end, temporalResolution);

  const dataByDay = {};
  data.data.map((row) => {
    dataByDay[moment(row["day"]).valueOf()] = row;
  });

  return steps.reduce(
    (multiLineData, step: Moment) => {
      const day = xFormatDate(step, temporalResolution, steps[0], steps[steps.length - 1]);
      const dayRaw = step.format("YYYY-MM-DD");
      multiLineData.chartData.push({
        day,
        dayRaw,
        ...inputTabs.reduce((linesData, tab) => {
          linesData = {
            ...linesData,
            ...getInsightsDataPointsPerDay(step, temporalResolution, dataByDay, tab.metricType as MetricTypes, groups),
          };
          return linesData;
        }, {} as ProfitabilityDataPoint),
      });
      return multiLineData;
    },
    {
      chartData: [],
    } as { chartData: ReferencedMultiChartDataPoint[] }
  );
};

const getDataPoint = (day: Moment, metricType: string, pointsByDay) => {
  return pointsByDay?.[day.valueOf()]?.[metricType];
};

const getInsightsDataPointsPerDay = (
  day: Moment,
  temporalResolution: TemporalResolution,
  pointsByDay,
  metricType: MetricTypes,
  groups: string[]
) => {
  const returnData = {};

  groups.forEach((group) => {
    const thisYearValue = getDataPoint(day, group + metricType, pointsByDay);
    let sameDayLastYear = day.clone().subtract(1, "year");
    if (temporalResolution.resolution === TemporalResolutions.WEEKLY.resolution) {
      sameDayLastYear = day.clone().subtract(1, "year").startOf("week");
    }
    const lastYearValue = getDataPoint(sameDayLastYear, group + metricType, pointsByDay);

    returnData[`${group}${metricType}Current`] = thisYearValue ? parseFloat(thisYearValue) : null;
    returnData[`${group}${metricType}Reference`] = lastYearValue ? parseFloat(lastYearValue) : null;
  });

  return returnData;
};

export const aggregateRevenueSummaryData = (
  current: DashboardOutput,
  reference: DashboardOutput,
  timeRange: CustomTimeRange,
  tabs: ReferencedChartTabProps[],
  groups: string[]
): ReferencedChartTabProps[] => {
  const deriveMetric = (metricType, data) => {
    return data?.[metricType] || "";
  };

  return groups.reduce((result, group) => {
    const ts = tabs.map((tab) => {
      const metric = current.metrics.find((metric) => metric.name === tab.metricType);

      const metricValue = deriveMetric(group + tab.metricType, current.summary);
      const newTab = { title: "", tooltip: tab.tooltip, metricType: tab.metricType };

      // Bit of a hack to make the onSelect work nicely here
      newTab["metricType"] = group;
      newTab["title"] = `${group}`;

      newTab["metricValue"] =
        metricValue == null
          ? null
          : formatValue({
              value: metricValue.toString(),
              metricType: metric,
              withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
            });
      const metricReferenceValue = deriveMetric(group + tab.metricType, reference.summary);
      newTab["metricReferenceValue"] =
        metricReferenceValue == null
          ? null
          : formatValue({
              value: metricReferenceValue.toString(),
              metricType: metric,
              withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
            });
      const referenceData = getReferenceData(metricValue, metricReferenceValue, metric?.success_direction);
      if (referenceData) {
        newTab["metricReferenceSuccess"] = referenceData.metricReferenceSuccess;
        newTab["metricReferenceDirection"] = referenceData.metricReferenceDirection;
        newTab["metricReferenceValue"] = referenceData.metricReferenceValue;
      }
      return newTab;
    });

    return [...result, ...ts];
  }, [] as ReferencedChartTabProps[]);
};
